import { useEffect, useState } from "react";
import { Card, Pill } from "../components/ui";
import { supabase } from "../supabaseClient";
import { S, css } from "../theme";

function FDAUpdates() {
  const [updates, setUpdates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchedAt, setFetchedAt] = useState(null);

  async function loadUpdates() {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("fda_updates")
        .select("headline, headline_type, updates, fetched_at")
        .order("fetched_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw new Error(error.message);

      if (data) {
        setUpdates({
          headline: data.headline,
          headlineType: data.headline_type,
          updates: data.updates,
        });
        setFetchedAt(data.fetched_at);
      } else {
        setUpdates(null);
      }
    } catch (err) {
      setUpdates({ error: err.message });
    }
    setLoading(false);
  }

  useEffect(() => { loadUpdates(); }, []);

  // How long ago the cron last ran
  const timeLabel = fetchedAt
    ? (() => {
        const mins = Math.round((Date.now() - new Date(fetchedAt).getTime()) / 60000);
        if (mins < 60) return mins < 2 ? "Just updated" : mins + " min ago";
        const hrs = Math.round(mins / 60);
        return hrs === 1 ? "1 hour ago" : hrs + " hours ago";
      })()
    : "Loading...";

  return (
    <div style={{ padding: 40 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
        <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", margin: 0 }}>FDA & Legal Updates</h2>
        <button onClick={loadUpdates} disabled={loading} style={{ padding: "8px 16px", borderRadius: 100, border: "1.5px solid " + S.border, background: "transparent", fontSize: "0.78rem", fontWeight: 600, color: S.muted, cursor: loading ? "wait" : "pointer", fontFamily: "Inter, sans-serif" }}>
          {loading ? "Loading..." : "↻ Refresh"}
        </button>
      </div>
      <p style={{ color: S.muted, fontSize: "0.85rem", marginBottom: 32 }}>
        Updated daily · {timeLabel}
      </p>

      {loading && !updates && (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: "2rem", marginBottom: 16 }}>⚖️</div>
          <div style={{ fontWeight: 600, color: S.ink, marginBottom: 8 }}>Fetching latest peptide regulations...</div>
          <p style={{ color: S.muted, fontSize: "0.82rem" }}>Searching for current FDA and compounding updates</p>
        </Card>
      )}

      {updates?.error && (
        <Card style={{ background: S.accent2Light, border: "1px solid #fca5a555" }}>
          <p style={{ color: "#dc2626", fontSize: "0.85rem", margin: 0 }}>Couldn't load updates right now. <button onClick={() => loadUpdates()} style={{ color: S.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Try again</button></p>
        </Card>
      )}

      {updates && !updates.error && (
        <>
          <Card style={{ marginBottom: 28, background: updates.headlineType === "positive" ? S.accentLight : updates.headlineType === "negative" ? S.accent2Light : S.accent2Light, border: "1px solid " + (updates.headlineType === "positive" ? S.accent + "44" : updates.headlineType === "negative" ? S.accent2 + "44" : S.accent2 + "33") }}>
            <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
              <span style={{ fontSize: "1.5rem" }}>{updates.headlineType === "positive" ? "✅" : updates.headlineType === "negative" ? "🚨" : "📋"}</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: "0.95rem", color: S.ink, marginBottom: 4 }}>Current Status</div>
                <p style={{ fontSize: "0.88rem", color: S.ink2, lineHeight: 1.7, margin: 0 }}>{updates.headline}</p>
              </div>
            </div>
          </Card>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {(updates.updates || []).map((u, i) => (
              <Card key={i}>
                <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: u.type === "positive" ? S.accent : u.type === "negative" ? "#dc2626" : S.muted, flexShrink: 0, marginTop: 5 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{u.title}</span>
                      {u.important && <Pill color={S.accent2} bg={S.accent2Light}>Important</Pill>}
                    </div>
                    <p style={{ fontSize: "0.83rem", color: S.muted, lineHeight: 1.7, margin: 0 }}>{u.summary}</p>
                    <div style={{ fontSize: "0.72rem", color: S.muted, marginTop: 10, fontWeight: 500 }}>{u.date}</div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Card style={{ marginTop: 24, background: S.surface2 }}>
        <p style={{ fontSize: "0.78rem", color: S.muted, lineHeight: 1.7, margin: 0 }}>
          <strong style={{ color: S.ink }}>Disclaimer:</strong> AI-generated summaries for educational purposes only. Verify with official FDA sources and consult a physician before starting any protocol.
        </p>
      </Card>
    </div>
  );
}

// ─── SETTINGS ────────────────────────────────────────────────

export { FDAUpdates };
